import { useRef, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { scrollStore } from "@/utils/scrollStore";

const lerp = THREE.MathUtils.lerp;
function ss(a, b, x) { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }
function mk(geo, mat, pos, rot) {
  const m = new THREE.Mesh(geo, mat);
  if (pos) m.position.set(pos[0], pos[1], pos[2]);
  if (rot) m.rotation.set(rot[0], rot[1], rot[2]);
  return m;
}
const H = Math.PI / 2;

/* ── Canvas texture: rotating crypto-token rim around the base plate ──
   The base plate rotates with the engine; this texture wraps around its
   outer edge so token tickers sweep past as the engine turns. ───────── */
function makeTokenRingTexture() {
  // Wide strip — will wrap around the cylinder side (U wraps 0→1 once).
  const w = 4096, h = 192;
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");

  // Background — transparent so the rim shows the brushed metal underneath;
  // we instead paint a semi-transparent dark overlay for readability.
  ctx.fillStyle = "rgba(6, 10, 16, 0.78)";
  ctx.fillRect(0, 0, w, h);

  // Thin cyan guide rails (top + bottom)
  ctx.fillStyle = "rgba(0, 216, 232, 0.55)";
  ctx.fillRect(0, 6, w, 2);
  ctx.fillRect(0, h - 8, w, 2);

  const tokens = ["BTC", "ETH", "SOL", "QVX", "POL", "AVAX", "LINK", "ARB", "OP", "DOGE", "BNB", "XRP"];
  const slot = w / tokens.length;

  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  tokens.forEach((tk, i) => {
    const cx = slot * (i + 0.5);
    const isQVX = tk === "QVX";

    // Separator diamonds between tokens
    ctx.fillStyle = "rgba(0, 216, 232, 0.75)";
    const sepX = slot * i;
    ctx.save();
    ctx.translate(sepX, h / 2);
    ctx.rotate(Math.PI / 4);
    ctx.fillRect(-5, -5, 10, 10);
    ctx.restore();

    // Token ticker — QVX highlighted in brighter cyan/white + "live" dot
    if (isQVX) {
      ctx.fillStyle = "#00f0ff";
      ctx.shadowColor = "#00d4e4";
      ctx.shadowBlur = 30;
      ctx.font = 'bold 120px "Space Grotesk","Helvetica Neue",Arial,sans-serif';
      ctx.fillText(tk, cx, h / 2 + 4);
      ctx.shadowBlur = 0;
      // Small live indicator dot above
      ctx.fillStyle = "#B5FF00";
      ctx.beginPath();
      ctx.arc(cx + 110, h / 2 - 30, 6, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillStyle = "rgba(220, 240, 246, 0.92)";
      ctx.font = 'bold 96px "Space Grotesk","Helvetica Neue",Arial,sans-serif';
      ctx.fillText(tk, cx, h / 2 + 4);
    }

    // Sub-label under each ticker
    ctx.fillStyle = "rgba(0, 216, 232, 0.55)";
    ctx.font = '24px "IBM Plex Mono",monospace';
    ctx.fillText(isQVX ? "QOVARIX" : "MARKET", cx, h / 2 + 64);
  });

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/* ── Canvas texture: central flip-chip / package-style die face ──
   Looks like a real GPU heat-spreader or lid: BGA-style pads, metal
   straps, one orientation corner, fine routing — not decorative “art”.
   QVX is etched into the center and does not move; texture is clamped. */
function makeQVXTexture() {
  const size = 1024;
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const ctx = c.getContext("2d");
  const pad = 48;
  const inner = size - pad * 2;

  // Substrate: dark package green / silicon
  const baseGrad = ctx.createLinearGradient(0, 0, size, size);
  baseGrad.addColorStop(0, "#0a0f12");
  baseGrad.addColorStop(0.5, "#06090c");
  baseGrad.addColorStop(1, "#030508");
  ctx.fillStyle = baseGrad;
  ctx.fillRect(0, 0, size, size);

  // Slight vignette
  const vig = ctx.createRadialGradient(size / 2, size / 2, inner * 0.15, size / 2, size / 2, inner * 0.7);
  vig.addColorStop(0, "rgba(0, 30, 40, 0.2)");
  vig.addColorStop(1, "rgba(0, 0, 0, 0.5)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, size, size);

  // Scribe / package outline (laser or saw street)
  ctx.strokeStyle = "rgba(0, 180, 200, 0.45)";
  ctx.lineWidth = 1;
  ctx.strokeRect(pad - 0.5, pad - 0.5, size - pad * 2 + 1, size - pad * 2 + 1);
  ctx.strokeStyle = "rgba(60, 80, 100, 0.6)";
  ctx.strokeRect(pad + 8, pad + 8, size - (pad + 8) * 2, size - (pad + 8) * 2);

  // BGA-style solder bumps along the four edges
  const bgaStep = 14;
  const bgaR = 3.2;
  const e0 = pad + 34;
  const e1 = size - pad - 34;
  const strip = 3;
  ctx.fillStyle = "rgba(180, 200, 210, 0.34)";
  const dot = (x, y) => {
    ctx.beginPath();
    ctx.arc(x, y, bgaR, 0, Math.PI * 2);
    ctx.fill();
  };
  for (let t = e0; t <= e1; t += bgaStep) {
    for (let s = 0; s < strip; s++) { dot(t, e0 + s * 8); dot(t, e1 - s * 8); }
  }
  for (let t = e0; t <= e1; t += bgaStep) {
    for (let s = 0; s < strip; s++) { dot(e0 + s * 8, t); dot(e1 - s * 8, t); }
  }

  // Metal1 / M2: dense rectilinear routing (not decorative fans)
  ctx.strokeStyle = "rgba(0, 200, 220, 0.22)";
  ctx.lineWidth = 1;
  for (let i = 0; i < 32; i++) {
    const x = 120 + (i * (size - 240) / 31);
    ctx.beginPath();
    ctx.moveTo(x, 130);
    ctx.lineTo(x, 420);
    ctx.moveTo(x, size - 130);
    ctx.lineTo(x, size - 420);
    ctx.stroke();
  }
  for (let j = 0; j < 32; j++) {
    const y = 120 + (j * (size - 240) / 31);
    ctx.beginPath();
    ctx.moveTo(130, y);
    ctx.lineTo(420, y);
    ctx.moveTo(size - 130, y);
    ctx.lineTo(size - 420, y);
    ctx.stroke();
  }
  // Via / stitch dots at grid crossings (sparse)
  ctx.fillStyle = "rgba(0, 190, 210, 0.45)";
  for (let i = 6; i < 26; i++) {
    for (let j = 6; j < 26; j++) {
      if ((i + j) % 3 === 0) {
        const vx = 120 + (i * (size - 240) / 25);
        const vy = 120 + (j * (size - 240) / 25);
        ctx.beginPath();
        ctx.arc(vx, vy, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // Orientation: single gold-triangle mark (one corner) — industry typical
  ctx.fillStyle = "rgba(255, 200, 80, 0.9)";
  ctx.beginPath();
  ctx.moveTo(pad + 24, pad + 18);
  ctx.lineTo(pad + 24, pad + 46);
  ctx.lineTo(pad + 50, pad + 32);
  ctx.closePath();
  ctx.fill();

  // Central heat spreader / die inset (recessed dark square with metal bevel)
  const cx = size / 2;
  const cy = size / 2;
  const spread = 200;
  ctx.fillStyle = "rgba(4, 8, 12, 0.92)";
  ctx.fillRect(cx - spread + 2, cy - spread + 2, spread * 2 - 4, spread * 2 - 4);
  ctx.strokeStyle = "rgba(30, 90, 100, 0.85)";
  ctx.lineWidth = 2;
  ctx.strokeRect(cx - spread, cy - spread, spread * 2, spread * 2);
  ctx.strokeStyle = "rgba(0, 160, 180, 0.45)";
  ctx.lineWidth = 1;
  ctx.strokeRect(cx - spread + 5, cy - spread + 5, spread * 2 - 10, spread * 2 - 10);

  // QVX: etched (stroke + fill), fixed in texture — no UV animation
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = 'bold 200px "Space Grotesk","Helvetica Neue",Arial,sans-serif';
  ctx.lineWidth = 3;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.7)";
  ctx.fillStyle = "#0bbfd4";
  ctx.strokeText("QVX", cx, cy - 6);
  ctx.fillText("QVX", cx, cy - 6);

  ctx.fillStyle = "rgba(0, 200, 220, 0.6)";
  ctx.font = '20px "IBM Plex Mono",monospace';
  ctx.fillText("QOVARIX  AI-X1  REV. 2.1B", cx, cy + 102);
  ctx.fillStyle = "rgba(0, 160, 180, 0.42)";
  ctx.font = "16px IBM Plex Mono, monospace";
  ctx.fillText("LOT  QVX-7F3C-A", cx, cy + 130);

  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 8;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.offset.set(0, 0);
  tex.center.set(0.5, 0.5);
  tex.needsUpdate = true;
  return tex;
}

export default function EngineModel({ onProgress }) {
  const { scene } = useThree();
  const R = useRef({});

  useEffect(() => {
    onProgress?.(35);

    /* ═══ MATERIALS ═══ */
    const matteHull = new THREE.MeshStandardMaterial({ color: 0x0a0e14, metalness: 0.78, roughness: 0.5 });
    const brushed   = new THREE.MeshStandardMaterial({ color: 0x141a24, metalness: 0.86, roughness: 0.32 });
    const polished  = new THREE.MeshStandardMaterial({ color: 0x2a3340, metalness: 0.96, roughness: 0.06 });
    const worn      = new THREE.MeshStandardMaterial({ color: 0x1a2030, metalness: 0.84, roughness: 0.48 });
    const rubber    = new THREE.MeshStandardMaterial({ color: 0x060809, metalness: 0.04, roughness: 0.88 });
    const glass     = new THREE.MeshStandardMaterial({ color: 0x06141e, transparent: true, opacity: 0.22, metalness: 0.08, roughness: 0.03 });
    // Chip substrate — dark silicon / PCB base
    const chipBase  = new THREE.MeshStandardMaterial({ color: 0x070b12, metalness: 0.9, roughness: 0.22 });
    const chipRim   = new THREE.MeshStandardMaterial({ color: 0x1a2230, metalness: 0.92, roughness: 0.18 });
    // Pin-grid pad (solder ball look) — bright cyan
    const pinGlow   = new THREE.MeshStandardMaterial({ color: 0x00e8f4, emissive: 0x00d4e4, emissiveIntensity: 0.85 });
    // Circuit trace material
    const traceMat  = new THREE.MeshStandardMaterial({ color: 0x00d8e8, emissive: 0x00c8dd, emissiveIntensity: 0.6 });
    const cyanSub   = new THREE.MeshStandardMaterial({ color: 0x00d8e8, emissive: 0x00c8dd, emissiveIntensity: 0.2 });
    // AI core
    const coreMat   = new THREE.MeshStandardMaterial({ color: 0x00e0f0, emissive: 0x00d0e0, emissiveIntensity: 0.6, metalness: 0.25, roughness: 0.35 });
    // Conduit (energy beam)
    const conduitMat = new THREE.MeshStandardMaterial({ color: 0x00e8f4, emissive: 0x00d0e0, emissiveIntensity: 1.0, transparent: true, opacity: 0.55 });
    const shadowMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5, depthWrite: false });

    // QVX die face material (canvas texture + emissive map)
    const qvxTex = makeQVXTexture();
    const qvxMat = new THREE.MeshStandardMaterial({
      map: qvxTex,
      emissiveMap: qvxTex,
      emissive: 0xffffff,
      emissiveIntensity: 0.9,
      metalness: 0.4,
      roughness: 0.32,
    });
    R.current.qvxMat = qvxMat;

    R.current.cyanSub = cyanSub;
    R.current.pinGlow = pinGlow;
    R.current.traceMat = traceMat;
    R.current.conduitMat = conduitMat;

    const engine = new THREE.Group();
    engine.rotation.set(0.13, Math.PI / 5.5, 0);
    R.current.engine = engine;

    /* ═══════════════════════════════════════
       1. CORE BODY — main cylindrical chassis
    ═══════════════════════════════════════ */
    const coreGrp = new THREE.Group();
    R.current.core = coreGrp;

    coreGrp.add(mk(new THREE.CylinderGeometry(0.95, 0.95, 1.55, 48), matteHull));
    coreGrp.add(mk(new THREE.CylinderGeometry(1.08, 1.08, 0.14, 44), brushed, [0, 0.6, 0]));
    coreGrp.add(mk(new THREE.CylinderGeometry(1.08, 1.08, 0.14, 44), brushed, [0, -0.6, 0]));
    coreGrp.add(mk(new THREE.TorusGeometry(0.96, 0.018, 10, 48), rubber, [0, 0.52, 0], [H, 0, 0]));
    coreGrp.add(mk(new THREE.TorusGeometry(0.96, 0.018, 10, 48), rubber, [0, -0.52, 0], [H, 0, 0]));
    [-0.38, -0.12, 0.14, 0.38].forEach(y =>
      coreGrp.add(mk(new THREE.TorusGeometry(0.97, 0.026, 10, 52), worn, [0, y, 0], [H, 0, 0]))
    );
    coreGrp.add(mk(new THREE.CylinderGeometry(0.68, 0.68, 1.15, 36), glass));
    const aiCore = mk(new THREE.IcosahedronGeometry(0.3, 2), coreMat);
    coreGrp.add(aiCore); R.current.aiCore = aiCore;
    coreGrp.add(mk(new THREE.CylinderGeometry(1.14, 1.14, 0.06, 48), polished, [0, 0.72, 0]));
    coreGrp.add(mk(new THREE.CylinderGeometry(1.14, 1.14, 0.06, 48), polished, [0, -0.72, 0]));
    coreGrp.add(mk(new THREE.TorusGeometry(1.14, 0.01, 10, 56), cyanSub, [0, 0.73, 0], [H, 0, 0]));
    coreGrp.add(mk(new THREE.TorusGeometry(1.14, 0.01, 10, 56), cyanSub, [0, -0.73, 0], [H, 0, 0]));
    for (let i = 0; i < 12; i++) {
      if (i === 5) continue;
      const a = (i / 12) * Math.PI * 2 + 0.1;
      coreGrp.add(mk(new THREE.CylinderGeometry(0.022, 0.022, 0.018, 6), worn, [1.02 * Math.cos(a), 0.76, 1.02 * Math.sin(a)]));
    }
    for (let i = 0; i < 12; i++) {
      if (i === 2) continue;
      const a = (i / 12) * Math.PI * 2;
      coreGrp.add(mk(new THREE.CylinderGeometry(0.022, 0.022, 0.018, 6), worn, [1.02 * Math.cos(a), -0.76, 1.02 * Math.sin(a)]));
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      coreGrp.add(mk(new THREE.BoxGeometry(0.005, 1.1, 0.005), cyanSub, [0.96 * Math.cos(a), 0, 0.96 * Math.sin(a)]));
    }

    const cl = new THREE.PointLight(0x00e0f0, 1.5, 9);
    coreGrp.add(cl); R.current.cl = cl;
    engine.add(coreGrp);

    /* ═══════════════════════════════════════
       2. ENERGY CONDUIT — AI core → chip die
    ═══════════════════════════════════════ */
    const conduit = mk(new THREE.CylinderGeometry(0.055, 0.07, 0.78, 20), conduitMat, [0, 0.45, 0]);
    engine.add(conduit);
    R.current.conduit = conduit;
    engine.add(mk(new THREE.CylinderGeometry(0.11, 0.11, 0.08, 24), brushed, [0, 0.78, 0]));
    engine.add(mk(new THREE.CylinderGeometry(0.11, 0.11, 0.08, 24), brushed, [0, 0.12, 0]));

    /* ═══════════════════════════════════════
       3. GPU CHIP MODULE — round CPU/GPU surface
       covers the entire top of the engine.
       ─────────────────────────────────────────
         • Circular substrate (full coverage)
         • Outer pin-grid: 2 concentric rings of
           glowing solder-ball dots
         • Radial & perimeter circuit traces
         • Central raised die (square) with the
           "QVX" glowing label.
    ═══════════════════════════════════════ */
    const chipHousing = new THREE.Group();
    chipHousing.position.y = 0.88;
    R.current.chipHousing = chipHousing;

    // 3a. Full-top circular substrate (large, flat, covers the entire engine top)
    const SUB_R = 1.14;
    chipHousing.add(mk(new THREE.CylinderGeometry(SUB_R, SUB_R, 0.06, 72), chipBase));
    // 3b. Outer raised rim
    chipHousing.add(mk(new THREE.TorusGeometry(SUB_R - 0.02, 0.025, 12, 72), chipRim, [0, 0.035, 0], [H, 0, 0]));
    // 3c. Inner accent ring
    chipHousing.add(mk(new THREE.TorusGeometry(SUB_R - 0.08, 0.005, 8, 72), cyanSub, [0, 0.046, 0], [H, 0, 0]));

    /* ── 3d. Pin-grid array ─ two concentric rings of
       small solder-ball dots around the perimeter ── */
    const ringDefs = [
      { r: SUB_R - 0.14, count: 64, size: 0.022 },
      { r: SUB_R - 0.22, count: 56, size: 0.020 },
    ];
    const pinMeshes = [];
    ringDefs.forEach((ring) => {
      for (let i = 0; i < ring.count; i++) {
        const a = (i / ring.count) * Math.PI * 2;
        const x = ring.r * Math.cos(a);
        const z = ring.r * Math.sin(a);
        const pinMat = pinGlow.clone();
        // Stagger base brightness so the grid isn't uniform
        pinMat.emissiveIntensity = 0.55 + ((i * 3) % 7) * 0.06;
        const pin = mk(new THREE.SphereGeometry(ring.size, 10, 10), pinMat, [x, 0.065, z]);
        chipHousing.add(pin);
        pinMeshes.push({ mesh: pin, mat: pinMat, angle: a, ring: ring.r });
      }
    });
    R.current.pinMeshes = pinMeshes;

    /* ── 3e. Radial circuit traces ─ 16 cardinal lines
       running from the pin-grid inward toward the die ── */
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const cx = Math.cos(a), cz = Math.sin(a);
      // Long radial trace
      const trace = mk(new THREE.BoxGeometry(0.44, 0.003, 0.004), traceMat,
        [cx * 0.68, 0.051, cz * 0.68], [0, -a, 0]);
      chipHousing.add(trace);
      // Small perpendicular tap at the inner end
      const tap = mk(new THREE.BoxGeometry(0.003, 0.003, 0.05), traceMat,
        [cx * 0.46, 0.051, cz * 0.46], [0, -a, 0]);
      chipHousing.add(tap);
    }

    /* ── 3f. Secondary trace ring (connects radial traces
       at mid-radius — reads as a clock distribution bus) ── */
    chipHousing.add(mk(new THREE.TorusGeometry(0.68, 0.003, 6, 72), traceMat, [0, 0.052, 0], [H, 0, 0]));
    chipHousing.add(mk(new THREE.TorusGeometry(0.48, 0.003, 6, 64), traceMat, [0, 0.052, 0], [H, 0, 0]));

    /* ── 3g. Central die (square) with QVX brand face ── */
    const DIE_SIZE = 0.78;
    const dieGrp = new THREE.Group();
    dieGrp.position.y = 0.08;
    // Die edge frame (slightly larger, lighter metal)
    dieGrp.add(mk(new THREE.BoxGeometry(DIE_SIZE + 0.1, 0.05, DIE_SIZE + 0.1), chipRim));
    // Die body (dark silicon)
    dieGrp.add(mk(new THREE.BoxGeometry(DIE_SIZE, 0.06, DIE_SIZE), chipBase, [0, 0.01, 0]));
    // Die face — QVX label plate (slightly inset on top)
    const dieFace = mk(new THREE.PlaneGeometry(DIE_SIZE - 0.04, DIE_SIZE - 0.04), qvxMat, [0, 0.041, 0], [-H, 0, 0]);
    dieGrp.add(dieFace);
    R.current.dieFace = dieFace;
    // Die corner hex bolts
    const dB = DIE_SIZE / 2 - 0.03;
    [[dB, 0.042, dB], [-dB, 0.042, dB], [dB, 0.042, -dB], [-dB, 0.042, -dB]].forEach((p) => {
      dieGrp.add(mk(new THREE.CylinderGeometry(0.018, 0.018, 0.008, 6), worn, p));
    });
    chipHousing.add(dieGrp);
    R.current.dieGrp = dieGrp;

    // 3h. Energy port ring — thin cyan ring around the die base (where conduit meets die)
    chipHousing.add(mk(new THREE.TorusGeometry(DIE_SIZE / 2 + 0.1, 0.006, 8, 48), pinGlow,
      [0, 0.055, 0], [H, 0, 0]));

    /* ── 3i. Transition collar below chip ── */
    const cap = mk(new THREE.CylinderGeometry(SUB_R - 0.04, SUB_R + 0.02, 0.07, 48), brushed, [0, -0.065, 0]);
    chipHousing.add(cap);

    /* ═══════════════════════════════════════
       4. BASE PLATFORM
    ═══════════════════════════════════════ */
    engine.add(chipHousing);

    const baseGrp = new THREE.Group();
    R.current.base = baseGrp;
    baseGrp.add(mk(new THREE.CylinderGeometry(2.0, 2.0, 0.10, 52), matteHull, [0, -1.1, 0]));
    baseGrp.add(mk(new THREE.CylinderGeometry(1.55, 1.55, 0.05, 44), brushed, [0, -1.05, 0]));
    baseGrp.add(mk(new THREE.TorusGeometry(2.0, 0.01, 8, 56), cyanSub, [0, -1.1, 0], [H, 0, 0]));
    baseGrp.add(mk(new THREE.TorusGeometry(1.7, 0.008, 6, 48), rubber, [0, -1.06, 0], [H, 0, 0]));

    /* ── Rotating crypto token rim around the outer edge of the base plate.
       The baseGrp is part of the engine group, so this rim sweeps past
       the viewer as the whole engine slowly rotates, surfacing all the
       supported markets (BTC, ETH, SOL, QVX, POL, AVAX, …). ───────── */
    const tokenTex = makeTokenRingTexture();
    const tokenMat = new THREE.MeshStandardMaterial({
      map: tokenTex,
      emissiveMap: tokenTex,
      emissive: 0xffffff,
      emissiveIntensity: 0.75,
      transparent: true,
      metalness: 0.3,
      roughness: 0.5,
      side: THREE.DoubleSide,
    });
    // Open-ended thin cylinder wrapping the outer rim
    const tokenGeo = new THREE.CylinderGeometry(2.02, 2.02, 0.14, 128, 1, true);
    const tokenRim = new THREE.Mesh(tokenGeo, tokenMat);
    tokenRim.position.set(0, -1.08, 0);
    baseGrp.add(tokenRim);
    R.current.tokenRim = tokenRim;
    R.current.tokenMat = tokenMat;
    R.current.tokenTex = tokenTex;

    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      baseGrp.add(mk(new THREE.CylinderGeometry(0.055, 0.055, 0.25, 8), matteHull, [1.5 * Math.cos(a), -1.3, 1.5 * Math.sin(a)]));
      baseGrp.add(mk(new THREE.CylinderGeometry(0.085, 0.085, 0.018, 8), rubber, [1.5 * Math.cos(a), -1.44, 1.5 * Math.sin(a)]));
    }
    baseGrp.add(mk(new THREE.CylinderGeometry(2.3, 2.3, 0.003, 44), shadowMat, [0, -1.48, 0]));
    const baseGlow = mk(new THREE.TorusGeometry(2.0, 0.004, 8, 56),
      new THREE.MeshBasicMaterial({ color: 0x00d0e0, transparent: true, opacity: 0.12, blending: THREE.AdditiveBlending, depthWrite: false }),
      [0, -1.12, 0], [H, 0, 0]);
    baseGrp.add(baseGlow); R.current.baseGlow = baseGlow;
    engine.add(baseGrp);

    scene.add(engine);
    onProgress?.(70);

    const refs = R.current;
    return () => {
      scene.remove(engine);
      engine.traverse(c => { if (c.geometry) c.geometry.dispose(); if (c.material) { if (Array.isArray(c.material)) c.material.forEach(m => m.dispose()); else c.material.dispose(); } });
      qvxTex.dispose?.();
      refs.tokenTex?.dispose?.();
    };
  }, [scene, onProgress]);

  /* ═══════════════════════════════════════
     ANIMATION — restrained engine-only breathing
     + QVX die pulse + radial pin-grid ripple.
  ═══════════════════════════════════════ */
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const p = scrollStore.progress;
    const ign = scrollStore.igniting;
    const iF = ign ? 1 : 0;
    const g = R.current;
    if (!g.engine) return;

    const breathPress = Math.sin(t * 0.6) * 0.006;
    const breathGlow  = Math.sin(t * 0.9) * 0.04;
    const breathSeam  = Math.sin(t * 1.1) * 0.025;
    const breathCore  = Math.sin(t * 1.6) * 0.012;

    const fullP   = ss(0.88, 1.0, p);
    const execP   = ss(0.50, 0.70, p);
    const settleP = ss(0.72, 0.88, p);
    const lockP   = fullP;

    /* Pre-compute core pulse (used by multiple blocks below) */
    const coreFreq = 1.6 + execP * 2.5 + lockP * 3;
    const corePulse = (Math.sin(t * coreFreq) + 1) * 0.5;  // 0 → 1

    /* Engine rotates + tilts on deep scroll/ignition */
    g.engine.rotation.y += 0.0022 + fullP * 0.004 + iF * 0.05;
    // Tilt progressively (x axis) to "reveal" the chip top as user scrolls
    const tiltT = ss(0.3, 0.7, p);
    g.engine.rotation.x = 0.13 + tiltT * 0.15 - iF * 0.05;
    g.engine.scale.setScalar(1);
    g.engine.position.y = 0;

    /* Chip housing micro pressure breathing */
    if (g.chipHousing) g.chipHousing.position.y = 0.88 + breathPress;

    /* AI Core — drives the chip */
    if (g.aiCore) {
      g.aiCore.scale.setScalar(1 + Math.sin(t * coreFreq) * 0.018 + breathCore + execP * 0.14 + lockP * 0.12 + iF * 0.4);
      g.aiCore.material.emissiveIntensity = 0.6 + breathGlow + execP * 3.5 + lockP * 4 + iF * 10;
      g.aiCore.rotation.x = t * 0.18;
      g.aiCore.rotation.z = t * 0.1;
    }

    /* Internal light */
    if (g.cl) g.cl.intensity = 1.5 + breathGlow * 1.5 + execP * 4 + lockP * 8 + iF * 25;

    /* Conduit pulses with core */
    if (g.conduitMat) {
      g.conduitMat.emissiveIntensity = 0.9 + corePulse * 0.9 + execP * 2 + lockP * 3 + iF * 6;
      g.conduitMat.opacity = 0.45 + corePulse * 0.25 + lockP * 0.25 + iF * 0.3;
    }

    /* QVX die — locked to die UVs; only subtle time breathing (not scroll) */
    if (g.qvxMat) {
      const diePulse = (Math.sin(t * 0.9) * 0.5 + 0.5) * 0.08;
      g.qvxMat.emissiveIntensity = 0.88 + breathSeam * 0.25 + diePulse;
    }

    /* Pin grid ripple — radial wave that pulses outward, tied to the core */
    if (g.pinMeshes) {
      const basePin = 0.55 + execP * 0.4 + lockP * 0.8 + iF * 2;
      const speed = 1.2 + execP * 0.6 + iF * 1.4;
      for (let i = 0; i < g.pinMeshes.length; i++) {
        const pm = g.pinMeshes[i];
        // angular travelling wave (makes each ring shimmer around the circumference)
        const shimmer = (Math.sin(t * speed + pm.angle * 4 + pm.ring * 3) + 1) * 0.5;
        pm.mat.emissiveIntensity = basePin + shimmer * (0.4 + corePulse * 0.6) + breathSeam * 0.5;
      }
    }

    /* Trace glow breathing */
    if (g.traceMat) g.traceMat.emissiveIntensity = 0.55 + corePulse * 0.35 + execP * 0.4 + lockP * 0.6 + iF * 1.2;
    if (g.cyanSub)  g.cyanSub.emissiveIntensity  = 0.2 + breathSeam * 0.6 + execP * 0.4 + lockP * 0.6 + iF * 1.5;

    /* Token rim emissive breathing (the rim itself rotates because it's
       part of the engine group — tokens sweep past the viewer naturally).
       On scroll, rim emissive brightens and the base rotates faster via
       a counter-offset applied to the tokenRim itself. */
    if (g.tokenMat) {
      g.tokenMat.emissiveIntensity = 0.7 + breathSeam * 0.4 + corePulse * 0.2 + execP * 0.4 + lockP * 0.6 + iF * 1.2;
    }
    if (g.tokenRim) {
      // extra rotation stacked on top of the engine's rotation — tickers accelerate on scroll
      g.tokenRim.rotation.y += 0.004 + execP * 0.012 + lockP * 0.02 + iF * 0.06;
    }

    /* Base */
    if (g.base) {
      g.base.position.y = lerp(g.base.position.y, -settleP * 0.4 + lockP * 0.35, 0.04 + lockP * 0.04);
    }
    if (g.baseGlow) {
      g.baseGlow.material.opacity = 0.12 + breathGlow * 0.5 + lockP * 0.3 + iF * 0.5;
    }
  });

  return null;
}
